import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api/client';

export function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** GET `path` with `params`; refetches when they change and ignores stale responses.
 *  Pass path = null to skip. Returns { data, loading, error, reload }. */
export function useFetch(path, params) {
  const [state, setState] = useState({ data: null, loading: Boolean(path), error: '' });
  const key = JSON.stringify([path, params]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    api.get(path, params, controller.signal)
      .then((data) => setState({ data, loading: false, error: '' }))
      .catch((error) => {
        if (error.name !== 'AbortError') setState({ data: null, loading: false, error: error.message });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/** Server-side paginated list with filters and sorting.
 *  `fixed` filters are always sent; `filters` are user editable and reset to page 1 on change. */
export function usePaged(path, { fixed = {}, initialFilters = {}, initialSize = 25, initialOrdering = '' } = {}) {
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const [ordering, setOrdering] = useState(initialOrdering);
  const debouncedFilters = useDebounce(filters);
  const fixedKey = JSON.stringify(fixed);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setPage(1);
  }, [debouncedFilters, fixedKey, pageSize, ordering]);

  const result = useFetch(path, { ...fixed, ...debouncedFilters, page, page_size: pageSize, ordering });
  const setFilter = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));
  const sortBy = (field) => setOrdering((prev) => (prev === field ? `-${field}` : field));

  return {
    ...result, filters, setFilter, clearFilters: () => setFilters({}),
    page, setPage, pageSize, setPageSize, ordering, sortBy,
  };
}

/** Paginated list without filters or sorting (for example the weekly candidates table). */
export function usePagedSimple(path, params, initialSize = 10) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const key = JSON.stringify([path, params]);
  useEffect(() => setPage(1), [key, pageSize]);
  const result = useFetch(path, { ...params, page, page_size: pageSize });
  return { ...result, page, setPage, pageSize, setPageSize, ordering: '', sortBy: () => {} };
}

const pad = (n) => String(n).padStart(2, '0');
/** YYYY-MM-DD in the user's own time zone (toISOString would shift the day). */
export const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };

export const fmtMoney = (value) => {
  const n = Number(String(value ?? '').replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : '—';
};
export const fmtPct = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(1) + '%');
export const fmtNumber = (n) => Number(n || 0).toLocaleString();
export const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${m}/${d}/${y}`;
};
export const initials = (name = '') => name.split(/[\s@._-]+/).filter(Boolean).slice(0, 2)
  .map((p) => p[0].toUpperCase()).join('') || '?';
